import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { ChevronLeft, ChevronRight } from "lucide-react";

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

type DisabledDateInfo = {
  date: Date;
  tooltipText: string;
};

type CustomCalendarProps = {
  onSelect?: (date: Date) => void;
  primaryColor?: string;
  disableFromDate?: Date | null;
  disableToDate?: Date | null;
  customDisabledDates?: DisabledDateInfo[];
  initialSelectedDate?: Date | null;
};

export default function CustomCalendar({
  onSelect,
  primaryColor = "#10b981",
  disableFromDate = null,
  disableToDate = null,
  customDisabledDates = [],
  initialSelectedDate = null,
}: CustomCalendarProps) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialSelectedDate || today);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  const days: { day: number; monthOffset: number }[] = [];
  let dayCounter = 1;
  for (let i = 0; i < 42; i++) {
    if (i < firstDay) {
      days.push({ day: prevMonthDays - firstDay + 1 + i, monthOffset: -1 });
    } else if (i >= firstDay + daysInMonth) {
      days.push({ day: dayCounter++, monthOffset: 1 });
    } else {
      days.push({ day: i - firstDay + 1, monthOffset: 0 });
    }
  }

  function goToPrevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  }

  function goToNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  }

  function isDisabled(date: Date) {
    if (customDisabledDates.length > 0) {
      const dateStr = date.toISOString().split('T')[0];
      const isCustomDisabled = customDisabledDates.some(
        (d) => d.date.toISOString().split('T')[0] === dateStr
      );
      if (isCustomDisabled) {
        return true;
      }
    }

    if (disableFromDate && disableToDate) {
      return date >= disableFromDate && date <= disableToDate;
    }
    if (disableFromDate && !disableToDate) {
      return date >= disableFromDate;
    }
    if (!disableFromDate && disableToDate) {
      return date <= disableToDate;
    }
    return false;
  }

  function getTooltipForDate(date: Date): string | null {
    if (customDisabledDates.length > 0) {
      const dateStr = date.toISOString().split('T')[0];
      const disabledInfo = customDisabledDates.find(
        (d) => d.date.toISOString().split('T')[0] === dateStr
      );
      return disabledInfo ? disabledInfo.tooltipText : null;
    }
    return null;
  }

  return (
    <div className="inline-block border rounded-lg p-4 bg-background shadow">
      <div className="flex justify-between mb-2 items-center">
        <ChevronLeft className="h-5 w-5 mb-1 cursor-pointer" onClick={goToPrevMonth} />

        <div className="flex justify-between mb-2 items-center gap-2">
          <Select
            value={String(currentMonth)}
            onValueChange={(val) => setCurrentMonth(Number(val))}
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m, i) => (
                <SelectItem key={i} value={String(i)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(currentYear)}
            onValueChange={(val) => setCurrentYear(Number(val))}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 30 }, (_, i) => currentYear - 15 + i).map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ChevronRight className="h-5 w-5 mb-1 cursor-pointer" onClick={goToNextMonth} />
      </div>

      <div className="grid grid-cols-7 text-center text-sm font-medium mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 text-center gap-1">
        {days.map(({ day, monthOffset }, i) => {
          const dateObj = new Date(currentYear, currentMonth + monthOffset, day);
          const isSelected =
            selectedDate &&
            dateObj.getDate() === selectedDate.getDate() &&
            dateObj.getMonth() === selectedDate.getMonth() &&
            dateObj.getFullYear() === selectedDate.getFullYear();

          const disabled = isDisabled(dateObj);
          const tooltip = getTooltipForDate(dateObj);

          return (
            <div key={i} className="relative group">
              <button
                type="button"
                onClick={() => {
                  if (disabled) return;
                  setSelectedDate(dateObj);
                  onSelect?.(dateObj);
                  setCurrentMonth(currentMonth + monthOffset);
                  setCurrentYear(
                    monthOffset === -1 && currentMonth === 0
                      ? currentYear - 1
                      : monthOffset === 1 && currentMonth === 11
                      ? currentYear + 1
                      : currentYear
                  );
                }}
                disabled={disabled}
                style={isSelected ? { backgroundColor: primaryColor, color: "white" } : {}}
                className={`h-9 w-9 rounded-md ${
                  disabled
                    ? "opacity-30 cursor-not-allowed"
                    : !isSelected
                    ? monthOffset !== 0
                      ? "opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700"
                      : "hover:bg-gray-200 dark:hover:bg-gray-700"
                    : ""
                }`}
              >
                {day}
              </button>
              {tooltip && disabled && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-[#F4FAFC] text-gray-700 text-sm rounded-2xl border border-[#25BCD7] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 shadow-lg flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#25BCD7" strokeWidth="2" fill="none"/>
                    <path d="M12 8V12M12 16H12.01" stroke="#25BCD7" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{tooltip}</span>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-[6px] border-transparent border-t-[#25BCD7]"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
